import React, { useState, useEffect, useCallback } from 'react';
import './styles/App.css';
import './i18n/i18n';
import MainLayout from './components/MainLayout';
import InfoPage from './components/InfoPage';
import ConfirmationPage from './components/ConfirmationPage';
import ExtensionInfoPage from './components/ExtensionInfoPage';
import ExtensionCapacityPage from './components/ExtensionCapacityPage';
import type { SeatToGuestMap, RoomData, Guest, GroupContext } from './types';
import { useTranslation } from 'react-i18next';
import GuestList from './components/GuestList';
import { BOOKING_CODE_MAX_LENGTH, BOOKING_CODE_PATTERN } from './config';
import { apiBase as makeApiBase, apiFetch } from './utils/apiFetch';
import { useStompSeatMap } from './hooks/useStompSeatMap';
import { useSseSeatMap } from './hooks/useSseSeatMap';

const App: React.FC = () => {
  const { t } = useTranslation();
  const [bookingCode, setBookingCode] = useState<string>(() => sessionStorage.getItem('bookingCode') || '');
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !!sessionStorage.getItem('isLoggedIn'));
  const [roomData, setRoomData] = useState<RoomData | null>(() => {
    const storedRoomData = sessionStorage.getItem('roomData');
    return storedRoomData ? JSON.parse(storedRoomData) : null;
  });
  const [seatToGuestMap, setSeatToGuestMap] = useState<SeatToGuestMap>(() => {
    const storedSeatToGuestMap = sessionStorage.getItem('seatToGuestMap');
    return storedSeatToGuestMap ? JSON.parse(storedSeatToGuestMap) : {};
  });
  const [guestCodes, setGuestCodes] = useState<Guest[]>(() => {
    const stored = sessionStorage.getItem('guestCodes');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [groupContext, setGroupContext] = useState<GroupContext | null>(() => {
    const stored = sessionStorage.getItem('groupContext');
    if (stored) {
      try { return JSON.parse(stored); } catch { return null; }
    }
    return null;
  });
  const [loginError, setLoginError] = useState<string>("");
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(() => {
    const stored = sessionStorage.getItem('sessionExpiresAt');
    return stored ? parseInt(stored, 10) : null;
  });
  const [sessionWarningMins, setSessionWarningMins] = useState<number | null>(null);
  const bookingCodeInputRef = React.useRef<HTMLInputElement>(null);

  // Whether the current session is a coordinator re-login for an existing
  // reservation group. Derived solely from groupContext (which is itself
  // hydrated from sessionStorage on reload). The login flow only fetches the
  // group context when the backend's login response sets `extensionMode: true`,
  // so its presence is a sufficient signal — single source of truth.
  const extensionMode = !!groupContext;

  // Common API base for all backend requests
  const APIBASE = makeApiBase();

  // Define the login response type for strict typing
  interface LoginResponse {
    roomData: RoomData;
    seatToGuestMap: SeatToGuestMap;
    sessionId: string;
    eventId?: string;
    extensionMode?: boolean;
    reservationGroupId?: string;
    token?: string;
    expiresAt?: number;
  }

  // Fetch and persist the group context for the given booking code, if any.
  // Returns the group context (or null if none exists for this booking code).
  const fetchGroupContext = useCallback(async (code: string): Promise<GroupContext | null> => {
    try {
      const res = await apiFetch(`groups/by-booking-code/${encodeURIComponent(code)}`);
      if (!res.ok) {
        sessionStorage.removeItem('groupContext');
        setGroupContext(null);
        return null;
      }
      const ctx = (await res.json()) as GroupContext;
      sessionStorage.setItem('groupContext', JSON.stringify(ctx));
      setGroupContext(ctx);
      return ctx;
    } catch (e) {
      console.warn('fetchGroupContext failed', e);
      return null;
    }
  }, [APIBASE]);

  // Login handler (decoupled from websocket connection)
  const handleLogin = () => {
    if (loggingIn) return;
    setLoginError("");
    setLoggingIn(true);
    fetch(`${APIBASE}seatmap/${bookingCode}`)
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 400) {
            const error = await response.json();
            setLoginError(t(error.error) || error.error || t('loginErrorDefault'));
            throw new Error(error.error || error.message || 'Login failed');
          }
          if (response.status === 404) {
            setLoginError(t('invalidBookingCode') || 'Ungültiger Buchungscode. Bitte prüfen Sie Ihre Eingabe.');
            throw new Error('invalidBookingCode');
          }
          throw new Error('Login failed');
        }
        return response.json();
      })
      .then(async (data: LoginResponse) => {
        // Always persist eventId in roomData for reloads
        const roomDataWithEventId = { ...data.roomData, eventId: data.eventId };
        setRoomData(roomDataWithEventId);
        setSeatToGuestMap(data.seatToGuestMap || {});
        sessionStorage.setItem('bookingCode', bookingCode);
        sessionStorage.setItem('roomData', JSON.stringify(roomDataWithEventId));
        sessionStorage.setItem('sessionId', data.sessionId);
        // Persist JWT before any authenticated follow-up calls (e.g. fetchGroupContext)
        if (data.token) {
          sessionStorage.setItem('seatmapToken', data.token);
        }
        if (data.expiresAt) {
          sessionStorage.setItem('sessionExpiresAt', String(data.expiresAt));
          setSessionExpiresAt(data.expiresAt);
        }
        sessionStorage.setItem('isLoggedIn', 'true');
        sessionStorage.removeItem('guestListConfirmed');
        sessionStorage.removeItem('confirmedTable');
        sessionStorage.setItem('seatToGuestMap', JSON.stringify(data.seatToGuestMap || {}));
        if (data.extensionMode) {
          sessionStorage.removeItem('extensionInfoPageShown');
          setExtensionInfoPageShown(false);
          // Pull full group context BEFORE marking the session as logged-in so
          // that the guestCodes effect sees extensionMode=true immediately and
          // skips writing an unlocked coordinator entry (which would duplicate
          // the locked entry seeded by the extension-mode seeding effect).
          await fetchGroupContext(bookingCode);
        } else {
          sessionStorage.removeItem('groupContext');
          setGroupContext(null);
        }
        setIsLoggedIn(true);
        // Removed STOMP websocket connection logic from here
      })
      .catch((error) => {
        console.error('Login error:', error);
        setIsLoggedIn(false);
        setRoomData(null);
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('seatmapToken');
        sessionStorage.removeItem('sessionExpiresAt');
        setSessionExpiresAt(null);
        setSessionWarningMins(null);
        setLoginError(t(error.message) || 'Ungültiger Buchungscode. Bitte prüfen Sie Ihre Eingabe.');
        setTimeout(() => {
          bookingCodeInputRef.current?.focus();
          bookingCodeInputRef.current?.select();
        }, 100);
      })
      .finally(() => {
        setLoggingIn(false);
      });
  };

  // On mount, validate sessionId with backend
  useEffect(() => {
    const sessionId = sessionStorage.getItem('sessionId');
    if (sessionId) {
      const apiBase = APIBASE;
      fetch(`${apiBase}sessions/${sessionId}`)
        .then((res) => {
          if (!res.ok) {
            // Session is invalid or expired
            sessionStorage.clear();
            setGuestCodes([]);
            setBookingCode('');
            setIsLoggedIn(false);
            setRoomData(null);
          }
        })
        .catch(() => {
          // On network error, also treat as invalid session
          sessionStorage.clear();
          setIsLoggedIn(false);
          setRoomData(null);
        });
    }
  }, []);

  // Session expiry countdown: warn at ≤5 min, force-logout at 0
  useEffect(() => {
    if (!isLoggedIn || !sessionExpiresAt) return;
    const CHECK_INTERVAL_MS = 30_000;
    const WARN_THRESHOLD_MS = 5 * 60 * 1000;
    const check = () => {
      const remaining = sessionExpiresAt - Date.now();
      if (remaining <= 0) {
        sessionStorage.clear();
        setIsLoggedIn(false);
        setRoomData(null);
        setGuestCodes([]);
        setBookingCode('');
        setSessionExpiresAt(null);
        setSessionWarningMins(null);
      } else if (remaining <= WARN_THRESHOLD_MS) {
        setSessionWarningMins(Math.ceil(remaining / 60_000));
      } else {
        setSessionWarningMins(null);
      }
    };
    check(); // run immediately on mount / login
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isLoggedIn, sessionExpiresAt]);

  // Ensure bookingCode is in guestCodes[0] after login and validate it.
  // Skipped in extension mode: the locked-member seeding effect (below)
  // populates the coordinator at index 0 with displayName/locked metadata.
  useEffect(() => {
    if (extensionMode) return;
    if (isLoggedIn && bookingCode) {
      setGuestCodes(prev => {
        if (prev.length === 0 || prev[0]?.guestCode !== bookingCode) {
          const updated = [...prev];
          if (updated.length === 0) {
            updated.push({ guestCode: bookingCode, guestGuid: '', valid: null });
          } else {
            updated[0] = { ...updated[0], guestCode: bookingCode };
          }
          sessionStorage.setItem('guestCodes', JSON.stringify(updated));
          return updated;
        }
        return prev;
      });

      // Validate the booking code and update guestGuid/valid
      const validateBookingCode = async () => {
        try {
          const response = await apiFetch(`guests/${encodeURIComponent(bookingCode)}`);
          if (response.status === 200) {
            const data = await response.json();
            setGuestCodes(prev => {
              const updated = [...prev];
              updated[0] = {
                guestCode: bookingCode,
                guestGuid: data.guestGuid || data.guid || data.id || '',
                valid: true,
              };
              sessionStorage.setItem('guestCodes', JSON.stringify(updated));
              return updated;
            });
          } else {
            setGuestCodes(prev => {
              const updated = [...prev];
              updated[0] = { ...updated[0], valid: false };
              sessionStorage.setItem('guestCodes', JSON.stringify(updated));
              return updated;
            });
          }
        } catch {
          setGuestCodes(prev => {
            const updated = [...prev];
            updated[0] = { ...updated[0], valid: false };
            sessionStorage.setItem('guestCodes', JSON.stringify(updated));
            return updated;
          });
        }
      };
      validateBookingCode();
    }
  }, [isLoggedIn, bookingCode, setGuestCodes, extensionMode]);

  // Track info page state in React, not just sessionStorage
  const [infoPageShown, setInfoPageShown] = useState(() => sessionStorage.getItem('infoPageShown') === 'true');
  const [extensionInfoPageShown, setExtensionInfoPageShown] = useState(
    () => sessionStorage.getItem('extensionInfoPageShown') === 'true'
  );

  // Live STOMP subscription. The hook owns reconnect logic and keeps
  // sessionStorage in sync; we just consume the connection-state flag for
  // the optional connectivity toast (rendered below).
  const sessionId = sessionStorage.getItem('sessionId');
  const { connected: wsConnected, retriesExhausted: wsRetriesExhausted } = useStompSeatMap({
    enabled: !!isLoggedIn && !!roomData?.eventId && !!sessionId,
    sessionId,
    eventId: roomData?.eventId,
    onSeatMap: setSeatToGuestMap,
  });
  // SSE fallback: only engages if the STOMP reconnect loop has given up
  // (corporate proxies that block ws:// upgrades). Same payload semantics:
  // every event triggers a refetch of the authoritative seat map.
  const { connected: sseConnected } = useSseSeatMap({
    enabled: !!isLoggedIn && !!roomData?.eventId && !!sessionId && wsRetriesExhausted,
    sessionId,
    onSeatMap: setSeatToGuestMap,
  });
  const liveConnected = wsConnected || sseConnected;
  // Show "connection lost" toast only after we've been connected at least
  // once, so it doesn't flash during the initial handshake.
  const [hadConnection, setHadConnection] = useState(false);
  useEffect(() => { if (liveConnected) setHadConnection(true); }, [liveConnected]);

  // Handle 401 responses: apiFetch signals session expiry via this event so
  // the React tree resets to the login screen without a page reload (which
  // would abort any in-flight mutations such as the confirm-assignment POST).
  // Resets ALL derived state so re-login starts from a clean slate — equivalent
  // to what location.reload() used to provide, but without killing in-flight requests.
  useEffect(() => {
    const handleSessionExpired = () => {
      setIsLoggedIn(false);
      setRoomData(null);
      setGuestCodes([]);
      setBookingCode('');
      setSessionExpiresAt(null);
      setSessionWarningMins(null);
      setInfoPageShown(false);
      setExtensionInfoPageShown(false);
      setHadConnection(false);
      setGroupContext(null);
      setGuestListConfirmed(false);
      setConfirmedTable(null);
      sessionStorage.removeItem('guestListConfirmed');
      sessionStorage.removeItem('confirmedTable');
      sessionStorage.removeItem('extensionInfoPageShown');
    };
    window.addEventListener('session:expired', handleSessionExpired);
    return () => window.removeEventListener('session:expired', handleSessionExpired);
  }, []);

  // Helper: render caption bar
  const renderCaptionBar = () => (
    <div className="caption-bar">
      <div className="caption-bar-logo">
        <img src="./images/Logo_GVNW.svg" alt="Logo" />
        <div className="apptitle-box"><span className="app-title">{t("appTitle")}</span><br/><span className="app-subtitle">{t("appSubTitle")}</span></div>
      </div>
      <div className="caption-bar-actions">
        {/* No logout button on login/info screens */}
      </div>
    </div>
  );

  // Helper: render footer
  const renderFooter = () => (
    <div className="footer">
      <table><tbody><tr>
        <td>
          <img src="./images/system/footer-logo.png" alt="Logo" className="footer-logo" />
        </td>
        <td>
          &copy; 2026 TablePlanner.com
        </td>
      </tr></tbody></table>
    </div>
  );

  // Track confirmation/info page state for guest list — persisted so a
  // page reload stays on the confirmation screen instead of going back to room view.
  const [guestListConfirmed, setGuestListConfirmed] = useState(() => sessionStorage.getItem('guestListConfirmed') === 'true');
  const [confirmedTable, setConfirmedTable] = useState<string | null>(() => sessionStorage.getItem('confirmedTable'));

  const terminateSession = useCallback(() => {
    sessionStorage.clear();
    setIsLoggedIn(false);
    setRoomData(null);
    setSeatToGuestMap({});
    setGuestCodes([]);
    setBookingCode('');
    setGroupContext(null);
    setInfoPageShown(false);
    setExtensionInfoPageShown(false);
    setGuestListConfirmed(false);
    setConfirmedTable(null);
    setSessionExpiresAt(null);
    setSessionWarningMins(null);
  }, []);

  const activeMemberCount = (groupContext?.members || []).filter(m => m.active).length;
  const extensionAtMaxCapacity = !!groupContext
    && activeMemberCount >= (groupContext.group.maxTotalGuests ?? Number.MAX_SAFE_INTEGER);

  // When entering extension mode (groupContext loaded), seed guestCodes
  // with the locked initial members so the GuestList can show them as
  // read-only rows alongside any newly added members.
  useEffect(() => {
    if (!extensionMode || !groupContext) return;
    const coordinatorId = groupContext.group.coordinatorGuestId;
    const locked = (groupContext.members || [])
      .filter(m => m.memberType === 'LOCKED_INITIAL' && m.active)
      // Ensure coordinator is always first regardless of DB return order.
      .sort((a, b) => {
        if (a.guestId === coordinatorId) return -1;
        if (b.guestId === coordinatorId) return 1;
        return 0;
      });
    if (locked.length === 0) return;
    setGuestCodes(prev => {
      // Build locked entries from group context members + current assignments.
      const assignmentByGuest: Record<string, string> = {};
      for (const a of (groupContext.currentAssignments || [])) {
        if (a.guestId) assignmentByGuest[a.guestId] = a.seatId;
      }
      const lockedEntries: Guest[] = locked.map(m => ({
        guestCode: '',
        guestGuid: m.guestId,
        valid: true,
        locked: true,
        displayName: m.bookingCode ?? '',
        seatGuid: assignmentByGuest[m.guestId],
      }));
      // Keep any existing non-locked entries the user may have added,
      // but exclude any entry whose guestGuid is already covered by a
      // locked entry (prevents the coordinator appearing twice when the
      // seeding effect fires after the initial unlocked entry was written).
      const lockedGuids = new Set(lockedEntries.map(e => e.guestGuid).filter(Boolean));
      const additional = prev.filter(p => !p.locked && (!p.guestGuid || !lockedGuids.has(p.guestGuid)));
      const merged = [...lockedEntries, ...additional];
      sessionStorage.setItem('guestCodes', JSON.stringify(merged));
      return merged;
    });
  }, [extensionMode, groupContext]);

  // Show login first
  if (!isLoggedIn) {
    return (
      <div className="main-layout">
        {renderCaptionBar()}
        <div className="login-container">
          <p className="login-title">{t('loginTitle')}</p>
          <p className="login-intro" style={{ whiteSpace: 'pre-line' }}>{t('loginIntro')}</p>
          <p className="login-title2">{t('loginTitle1')}</p>
          <p className="login-info">{t('loginInfo')}</p>
          <input
            type="text"
            value={bookingCode}
            onChange={(e) => setBookingCode(
              e.target.value.toUpperCase().replace(BOOKING_CODE_PATTERN, '').slice(0, BOOKING_CODE_MAX_LENGTH)
            )}
            placeholder={t('bookingCode')}
            className="booking-code-input"
            maxLength={BOOKING_CODE_MAX_LENGTH}
            ref={bookingCodeInputRef}
            disabled={loggingIn}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="one-time-code"
            spellCheck={false}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === 'Return') {
                handleLogin();
              }
            }}
          />
          <button
            onClick={handleLogin}
            className="login-button"
            disabled={loggingIn || bookingCode.length === 0}
            aria-busy={loggingIn}
          >
            {loggingIn ? (t('loggingIn') || 'Anmelden...') : t('login')}
          </button>
          <div style={{ minHeight: 28, marginTop: 8 }}>
            {loginError && (
              <div className="login-error">{loginError}</div>
            )}
          </div>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Show initial flow info page only for first-time/non-extension sessions.
  if (!extensionMode && !infoPageShown) {
    return (
      <MainLayout
        roomData={roomData}
        seatToGuestMap={seatToGuestMap}
        setSeatToGuestMap={setSeatToGuestMap}
        guestCodes={guestCodes}
        setGuestCodes={setGuestCodes}
        logo="./images/Logo_GVNW.svg"
        footerLogo="./images/system/footer-logo.png"
        appName="Sitzplatz-Auswahl"
        wsConnected={true}
      >
        <InfoPage
          t={t}
          onContinue={() => {
            sessionStorage.setItem('infoPageShown', 'true');
            setInfoPageShown(true);
          }}
        />
      </MainLayout>
    );
  }

  // Show dedicated coordinator re-login guidance in extension mode.
  if (extensionMode && groupContext && !extensionInfoPageShown) {
    return (
      <MainLayout
        roomData={roomData}
        seatToGuestMap={seatToGuestMap}
        setSeatToGuestMap={setSeatToGuestMap}
        guestCodes={guestCodes}
        setGuestCodes={setGuestCodes}
        logo="./images/Logo_GVNW.svg"
        footerLogo="./images/system/footer-logo.png"
        appName="Sitzplatz-Auswahl"
        wsConnected={true}
      >
        <ExtensionInfoPage
          t={t}
          groupContext={groupContext}
          onContinue={() => {
            sessionStorage.setItem('extensionInfoPageShown', 'true');
            setExtensionInfoPageShown(true);
          }}
        />
      </MainLayout>
    );
  }

  if (guestListConfirmed) {
    return (
      <MainLayout
        roomData={roomData}
        seatToGuestMap={seatToGuestMap}
        setSeatToGuestMap={setSeatToGuestMap}
        guestCodes={guestCodes}
        setGuestCodes={setGuestCodes}
        logo="./images/Logo_GVNW.svg"
        footerLogo="./images/system/footer-logo.png"
        appName="Sitzplatz-Auswahl"
        wsConnected={true}
      >
        <ConfirmationPage
          t={t}
          table={confirmedTable}
        />
      </MainLayout>
    );
  }

  // Extension mode guard: group already at max capacity.
  // Login is allowed for visibility, but no extension action is possible.
  // This guard is evaluated after the confirmation route so a successful
  // extension confirm always lands on the confirmation page.
  if (extensionMode && groupContext && extensionAtMaxCapacity) {
    return (
      <MainLayout
        roomData={roomData}
        seatToGuestMap={seatToGuestMap}
        setSeatToGuestMap={setSeatToGuestMap}
        guestCodes={guestCodes}
        setGuestCodes={setGuestCodes}
        logo="./images/Logo_GVNW.svg"
        footerLogo="./images/system/footer-logo.png"
        appName="Sitzplatz-Auswahl"
        wsConnected={true}
      >
        <ExtensionCapacityPage
          t={t}
          groupContext={groupContext}
          onTerminateSession={terminateSession}
        />
      </MainLayout>
    );
  }


  return (
    <div style={{ position: 'relative' }}>
      {sessionWarningMins !== null && (
        <div className={`session-expiry-banner${sessionWarningMins <= 2 ? ' urgent' : ''}`}>
          <span>{t('sessionExpiryWarning', { minutes: sessionWarningMins })}</span>
          <button onClick={() => setSessionWarningMins(null)}>{t('sessionExpiryDismiss')}</button>
        </div>
      )}
      <MainLayout
        roomData={roomData}
        seatToGuestMap={seatToGuestMap}
        setSeatToGuestMap={setSeatToGuestMap}
        guestCodes={guestCodes}
        setGuestCodes={setGuestCodes}
        logo="./images/Logo_GVNW.svg"
        footerLogo="./images/system/footer-logo.png"
        appName="Sitzplatz-Auswahl"
        wsConnected={liveConnected}
        extensionMode={extensionMode}
        groupContext={groupContext}
        refreshGroupContext={() => fetchGroupContext(bookingCode)}
        onAssignmentsConfirmed={ (table) => {
          setConfirmedTable(table);
          setGuestListConfirmed(true);
          if (table) sessionStorage.setItem('confirmedTable', table);
          else sessionStorage.removeItem('confirmedTable');
          sessionStorage.setItem('guestListConfirmed', 'true');
        } }

      />
    </div>
  );
};

export default App;

